import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import type {
  OoseItemResolution,
  OosePendingRenewalAction,
  ReratingStatus,
  OoseConflictResolution,
  OoseResolutionItem,
  OoseFieldConflict,
  OoseFieldItemGroup,
  OoseResolutionDetail,
  PatchOoseItemRequest,
} from '@lio/api-types';
import { ooseResolutionQueryKeys } from './useGetOoseResolution';

export type {
  OoseItemResolution,
  OosePendingRenewalAction,
  ReratingStatus,
  OoseConflictResolution,
  OoseFieldConflict,
  OoseFieldItemGroup,
  OoseResolutionItem,
};

// PATCH /oose-resolution/:id/item/:itemId
export const usePatchOoseResolutionItem = ({ policyId }: { policyId: string }) => {
  const queryClient = useQueryClient();
  const client = useClient();

  const result = useMutation({
    mutationFn: ({
      resolutionId,
      itemId,
      body,
    }: {
      resolutionId: string;
      itemId: string;
      body: PatchOoseItemRequest;
    }) => client.patch(`oose-resolution/${resolutionId}/item/${itemId}`, { json: body }).json<OoseResolutionItem>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ooseResolutionQueryKeys.get(policyId) });
    },
  });

  return { ...result, patchOoseResolutionItem: result.mutateAsync };
};

// POST /oose-resolution/:id/commit
export const useCommitOoseResolution = ({ policyId }: { policyId: string }) => {
  const queryClient = useQueryClient();
  const client = useClient();

  const result = useMutation({
    mutationFn: ({ resolutionId }: { resolutionId: string }) =>
      // OOSE processing can include several sequential OneShield operations.
      // Do not let the shared 30-second browser timeout turn an in-flight,
      // resumable commit into a misleading client error.
      client.post(`oose-resolution/${resolutionId}/commit`, { timeout: false }).json<OoseResolutionDetail>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ooseResolutionQueryKeys.get(policyId) });
      queryClient.invalidateQueries({ queryKey: ['policy'] });
      queryClient.invalidateQueries({ queryKey: ['policyTransactions'] });
    },
    // A failed commit can still have reconciled an edited or newly-bound
    // renewal. Refresh so the summary presents the new required action.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ooseResolutionQueryKeys.get(policyId) });
    },
  });

  return { ...result, commitOoseResolution: result.mutateAsync };
};

// POST /oose-resolution/:id/revert
export const useRevertOoseResolution = ({ policyId }: { policyId: string }) => {
  const queryClient = useQueryClient();
  const client = useClient();

  const result = useMutation({
    mutationFn: ({ resolutionId }: { resolutionId: string }) =>
      client.post(`oose-resolution/${resolutionId}/revert`).json<OoseResolutionDetail>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ooseResolutionQueryKeys.get(policyId) });
      queryClient.invalidateQueries({ queryKey: ['policy'] });
      queryClient.invalidateQueries({ queryKey: ['policyTransactions'] });
    },
  });

  return { ...result, revertOoseResolution: result.mutateAsync };
};

// POST /oose-resolution/:id/item/:itemId/reset
export const useResetOoseResolutionItem = ({ policyId }: { policyId: string }) => {
  const queryClient = useQueryClient();
  const client = useClient();

  const result = useMutation({
    mutationFn: ({ resolutionId, itemId }: { resolutionId: string; itemId: string }) =>
      client.post(`oose-resolution/${resolutionId}/item/${itemId}/reset`).json<OoseResolutionItem>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ooseResolutionQueryKeys.get(policyId) });
    },
  });

  return { ...result, resetOoseResolutionItem: result.mutateAsync };
};

// POST /oose-resolution/:id/reset
export const useResetOoseResolution = ({ policyId }: { policyId: string }) => {
  const queryClient = useQueryClient();
  const client = useClient();

  const result = useMutation({
    mutationFn: ({ resolutionId }: { resolutionId: string }) =>
      client.post(`oose-resolution/${resolutionId}/reset`).json<OoseResolutionDetail>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ooseResolutionQueryKeys.get(policyId) });
    },
  });

  return { ...result, resetOoseResolution: result.mutateAsync };
};
